import React, { useState, useEffect, useCallback } from 'react';
import {
  Trash2, Download, Eye, Search, X,
  Printer, RefreshCw, CheckCircle, Clock,
  Plus, Minus, FileText
} from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import { getAllInvoices, markInvoicePaid, deleteInvoice, createInvoice, getServices, getMechanics } from '../api';
import { downloadInvoicePdf, printInvoicePdf } from '../utils/PdfInvoice';
import './BillingPage.css';

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

function calcTotals(inv) {
  const subtotal   = Number(inv.subtotal   || 0);
  const grandTotal = Number(inv.grandTotal || 0);
  const gstAmount  = Number(inv.gstAmount  || 0);
  // Split GST: spare parts always 18%, services+labour at configured rate
  const partsTotal = (inv.spareParts || []).reduce((s, p) => s + Number(p.lineTotal || 0), 0);
  const slBase     = subtotal - partsTotal;
  const gst        = inv.gstPercent || 18;
  const partsGst   = Number(inv.partsGstAmount  || Math.round(partsTotal * 18 / 100));
  const svcsGst    = Number(inv.svcsGstAmount   || Math.round(slBase * gst / 100));
  return { subtotal, gstAmount, grandTotal, partsTotal, partsGst, svcsGst, gst };
}

function InvoiceViewModal({ inv, onClose, onMarkPaid }) {
  const { subtotal, gstAmount, grandTotal, partsTotal, partsGst, svcsGst, gst } = calcTotals(inv);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal invoice-modal" onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <h3 className="modal__title">{inv.id}</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{inv.customerName} · {inv.vehicleDisplay}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => downloadInvoicePdf(inv)}><Download size={13} /> PDF</button>
            <button className="btn btn-outline btn-sm" onClick={() => printInvoicePdf(inv)}><Printer size={13} /> Print</button>
            {inv.status !== 'paid' && (
              <button className="btn btn-primary btn-sm" onClick={() => onMarkPaid(inv.id)}><CheckCircle size={13} /> Mark Paid</button>
            )}
            <button className="modal__close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="invoice-view">
          <div className="invoice-view__header">
            <div>
              <p style={{ fontSize: 20, fontFamily: 'var(--font-heading)', fontWeight: 500 }}>AutoElite</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>12, GT Road, Ludhiana · +91 12345 67890</p>
              <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>GSTIN: 03AABCU9603R1ZV</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 16 }}>{inv.id}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{inv.serviceDate || '—'}</p>
              <span className={`badge ${inv.status === 'paid' ? 'badge-success' : 'badge-warning'}`} style={{ marginTop: 6, display: 'inline-block' }}>
                {inv.status === 'paid' ? 'PAID' : 'PENDING'}
              </span>
            </div>
          </div>
          <div className="invoice-view__parties">
            <div>
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>BILL TO</p>
              <p style={{ fontWeight: 600 }}>{inv.customerName}</p>
              {inv.customerPhone && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.customerPhone}</p>}
              {inv.customerEmail && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.customerEmail}</p>}
            </div>
            <div>
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>VEHICLE</p>
              <p style={{ fontWeight: 600 }}>{inv.vehicleDisplay}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Reg: {inv.regNumber}</p>
              {inv.mechanicName && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Mechanic: {inv.mechanicName}</p>}
            </div>
          </div>
          <table className="invoice-view__table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Unit Price</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(inv.services || []).map((s, i) => (
                <tr key={i}>
                  <td>{s.name} <span className="inv-type-tag">Service</span></td>
                  <td style={{ textAlign: 'center' }}>1</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{inr(s.unitPrice)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{inr(s.amount)}</td>
                </tr>
              ))}
              {(inv.spareParts || []).map((p, i) => (
                <tr key={i}>
                  <td>{p.name} <span className="inv-type-tag inv-type-tag--part">Part</span></td>
                  <td style={{ textAlign: 'center' }}>{p.qty}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{inr(p.unitPrice)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{inr(p.lineTotal)}</td>
                </tr>
              ))}
              {Number(inv.labourCharges || 0) > 0 && (
                <tr>
                  <td>Labour Charges</td>
                  <td style={{ textAlign: 'center' }}>1</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{inr(inv.labourCharges)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{inr(inv.labourCharges)}</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="invoice-totals">
            <div className="invoice-total-row"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            {partsTotal > 0 ? (<>
              <div className="invoice-total-row"><span>GST on Services &amp; Labour @ {gst}%</span><span>{inr(svcsGst)}</span></div>
              <div className="invoice-total-row"><span>GST on Spare Parts @ 18%</span><span>{inr(partsGst)}</span></div>
            </>) : (
              <div className="invoice-total-row"><span>GST ({gst}%)</span><span>{inr(gstAmount)}</span></div>
            )}
            <div className="invoice-total-row invoice-total-row--grand"><span>Grand Total</span><span>{inr(grandTotal)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManualInvoiceModal({ onClose, onCreated }) {
  const [services,      setServices]      = useState([]);
  const [mechanics,     setMechanics]     = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [customerName,  setCustomerName]  = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [vehicleMake,   setVehicleMake]   = useState('');
  const [vehicleModel,  setVehicleModel]  = useState('');
  const [vehicleYear,   setVehicleYear]   = useState('');
  const [regNumber,     setRegNumber]     = useState('');
  const [fuelType,      setFuelType]      = useState('Petrol');
  const [mechanicName,  setMechanicName]  = useState('');
  const [serviceDate,   setServiceDate]   = useState(new Date().toISOString().split('T')[0]);
  const [serviceLines,  setServiceLines]  = useState([{ name: '', unitPrice: '' }]);
  const [partLines,     setPartLines]     = useState([]);
  const [labourCharges, setLabourCharges] = useState('');
  const [gstPercent,    setGstPercent]    = useState(18);

  useEffect(() => {
    getServices().catch(() => []).then(s => setServices(s || []));
    getMechanics().catch(() => []).then(m => setMechanics(m || []));
  }, []);

  const servicesSubtotal = serviceLines.reduce((s, l) => s + (parseFloat(l.unitPrice) || 0), 0);
  const partsSubtotal    = partLines.reduce((s, l) => s + ((parseFloat(l.unitPrice) || 0) * (parseInt(l.qty) || 1)), 0);
  const labour           = parseFloat(labourCharges) || 0;
  const subtotal         = servicesSubtotal + partsSubtotal + labour;
  const gstAmount        = +(subtotal * gstPercent / 100).toFixed(2);
  const grandTotal       = +(subtotal + gstAmount).toFixed(2);

  const addServiceLine    = () => setServiceLines(p => [...p, { name: '', unitPrice: '' }]);
  const removeServiceLine = i  => setServiceLines(p => p.filter((_, idx) => idx !== i));
  const updateServiceLine = (i, field, val) => setServiceLines(p => p.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const addPartLine       = () => setPartLines(p => [...p, { name: '', qty: 1, unitPrice: '' }]);
  const removePartLine    = i  => setPartLines(p => p.filter((_, idx) => idx !== i));
  const updatePartLine    = (i, field, val) => setPartLines(p => p.map((l, idx) => idx === i ? { ...l, [field]: val } : l));

  const handleSubmit = async () => {
    setError('');
    if (!customerName.trim()) return setError('Customer name is required.');
    if (!vehicleMake.trim() || !vehicleModel.trim()) return setError('Vehicle make and model are required.');
    if (!regNumber.trim()) return setError('Registration number is required.');
    if (serviceLines.every(l => !l.name.trim())) return setError('Add at least one service.');

    const validServices = serviceLines.filter(l => l.name.trim())
      .map(l => ({ name: l.name, unitPrice: parseFloat(l.unitPrice) || 0, amount: parseFloat(l.unitPrice) || 0 }));
    const validParts = partLines.filter(l => l.name.trim())
      .map(l => ({ name: l.name, qty: parseInt(l.qty) || 1, unitPrice: parseFloat(l.unitPrice) || 0, lineTotal: (parseFloat(l.unitPrice) || 0) * (parseInt(l.qty) || 1) }));

    const payload = {
      bookingId:      'WALKIN-' + Date.now(),
      customerName:   customerName.trim(),
      customerPhone:  customerPhone.trim(),
      customerEmail:  customerEmail.trim(),
      vehicleDisplay: `${vehicleMake} ${vehicleModel} ${vehicleYear}`.trim(),
      regNumber:      regNumber.trim(),
      fuelType,
      mechanicName:   mechanicName.trim(),
      serviceDate,
      services:       validServices,
      spareParts:     validParts,
      labourCharges:  labour || null,
      gstPercent,
    };

    setSaving(true);
    try {
      const created = await createInvoice(payload);
      onCreated(created);
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to create invoice.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal manual-invoice-modal" onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <h3 className="modal__title">New Manual Invoice</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Walk-in customer · no booking required</p>
          </div>
          <button className="modal__close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="manual-invoice-body">
          {/* Customer */}
          <div className="manual-section">
            <p className="manual-section__title">Customer Details</p>
            <div className="manual-grid-3">
              <div className="form-group">
                <label>Full Name *</label>
                <input className="input" placeholder="e.g. Rajiv Kumar" value={customerName} onChange={e => setCustomerName(e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input className="input" placeholder="+91 98765 43210" value={customerPhone} onChange={e => setCustomerPhone(e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="input" placeholder="optional" value={customerEmail} onChange={e => setCustomerEmail(e.target.value.toUpperCase())} />
              </div>
            </div>
          </div>

          {/* Vehicle */}
          <div className="manual-section">
            <p className="manual-section__title">Vehicle Details</p>
            <div className="manual-grid-3">
              <div className="form-group">
                <label>Make *</label>
                <input className="input" placeholder="e.g. Maruti" value={vehicleMake} onChange={e => setVehicleMake(e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label>Model *</label>
                <input className="input" placeholder="e.g. Swift" value={vehicleModel} onChange={e => setVehicleModel(e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label>Year</label>
                <input className="input" placeholder="e.g. 2022" value={vehicleYear} onChange={e => setVehicleYear(e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label>Reg Number *</label>
                <input className="input" placeholder="e.g. PB10AB1234" value={regNumber} onChange={e => setRegNumber(e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label>Fuel Type</label>
                <select className="input" value={fuelType} onChange={e => setFuelType(e.target.value)}>
                  {['Petrol','Diesel','CNG','Electric','Hybrid'].map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Service Date</label>
                <input className="input" type="date" value={serviceDate} onChange={e => setServiceDate(e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>Mechanic</label>
              <select className="input" value={mechanicName} onChange={e => setMechanicName(e.target.value)}>
                <option value="">— Select mechanic (optional) —</option>
                {mechanics.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            </div>
          </div>

          {/* Services */}
          <div className="manual-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <p className="manual-section__title" style={{ marginBottom: 0 }}>Services Performed *</p>
              <button className="btn btn-ghost btn-sm" onClick={addServiceLine}><Plus size={13} /> Add Row</button>
            </div>
            {serviceLines.map((line, i) => (
              <div key={i} className="manual-line-row">
                <select className="input" style={{ flex: 2 }} value={line.name}
                  onChange={e => {
                    const svc = services.find(s => s.name === e.target.value);
                    updateServiceLine(i, 'name', e.target.value);
                    if (svc) updateServiceLine(i, 'unitPrice', svc.price || '');
                  }}>
                  <option value="">— pick service —</option>
                  {services.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
                <input className="input" style={{ flex: 2 }} placeholder="Or type service name"
                  value={line.name} onChange={e => updateServiceLine(i, 'name', e.target.value.toUpperCase())} />
                <input className="input" style={{ flex: 1 }} placeholder="Price ₹"
                  type="number" min="0" value={line.unitPrice}
                  onChange={e => updateServiceLine(i, 'unitPrice', e.target.value)} />
                <button className="tbl-btn tbl-btn--danger" onClick={() => removeServiceLine(i)} disabled={serviceLines.length === 1}>
                  <Minus size={13} />
                </button>
              </div>
            ))}
          </div>

          {/* Parts */}
          <div className="manual-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <p className="manual-section__title" style={{ marginBottom: 0 }}>Spare Parts Used</p>
              <button className="btn btn-ghost btn-sm" onClick={addPartLine}><Plus size={13} /> Add Part</button>
            </div>
            {partLines.length === 0 && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No parts added.</p>}
            {partLines.map((line, i) => (
              <div key={i} className="manual-line-row">
                <input className="input" style={{ flex: 3 }} placeholder="Part name"
                  value={line.name} onChange={e => updatePartLine(i, 'name', e.target.value.toUpperCase())} />
                <input className="input" style={{ flex: 1 }} placeholder="Qty"
                  type="number" min="1" value={line.qty}
                  onChange={e => updatePartLine(i, 'qty', e.target.value)} />
                <input className="input" style={{ flex: 1 }} placeholder="Unit ₹"
                  type="number" min="0" value={line.unitPrice}
                  onChange={e => updatePartLine(i, 'unitPrice', e.target.value)} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 60, textAlign: 'right' }}>
                  {inr((parseFloat(line.unitPrice) || 0) * (parseInt(line.qty) || 1))}
                </span>
                <button className="tbl-btn tbl-btn--danger" onClick={() => removePartLine(i)}><Minus size={13} /></button>
              </div>
            ))}
          </div>

          {/* Labour + GST */}
          <div className="manual-section">
            <p className="manual-section__title">Charges & Tax</p>
            <div className="manual-grid-3">
              <div className="form-group">
                <label>Labour Charges (₹)</label>
                <input className="input" type="number" min="0" placeholder="0"
                  value={labourCharges} onChange={e => setLabourCharges(e.target.value)} />
              </div>
              <div className="form-group">
                <label>GST %</label>
                <select className="input" value={gstPercent} onChange={e => setGstPercent(Number(e.target.value))}>
                  {[0, 5, 12, 18, 28].map(g => <option key={g} value={g}>{g}%</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Live totals preview */}
          <div className="manual-totals-preview">
            <div className="manual-total-row"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="manual-total-row"><span>GST ({gstPercent}%)</span><span>{inr(gstAmount)}</span></div>
            <div className="manual-total-row manual-total-row--grand"><span>Grand Total</span><span>{inr(grandTotal)}</span></div>
          </div>

          {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>{error}</p>}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : <><FileText size={14} /> Generate Invoice</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BillingPage() {
  const [invoices,    setInvoices]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [viewInv,     setViewInv]     = useState(null);
  const [deleting,    setDeleting]    = useState({});
  const [showManual,  setShowManual]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllInvoices();
      const sorted = (data || []).slice().sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (tb !== ta) return tb - ta;
        const da = a.serviceDate ? new Date(a.serviceDate).getTime() : 0;
        const db = b.serviceDate ? new Date(b.serviceDate).getTime() : 0;
        if (db !== da) return db - da;
        return (b.id || '').localeCompare(a.id || '');
      });
      setInvoices(sorted);
    } catch (e) {
      console.error('Failed to load invoices:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkPaid = async (id) => {
    try {
      const updated = await markInvoicePaid(id);
      setInvoices(prev => prev.map(i => i.id === id ? updated : i));
      if (viewInv?.id === id) setViewInv(updated);
    } catch (e) {
      alert('Failed to mark paid: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    setDeleting(d => ({ ...d, [id]: true }));
    try {
      await deleteInvoice(id);
      setInvoices(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.message || e.message));
    } finally {
      setDeleting(d => ({ ...d, [id]: false }));
    }
  };

  const handleInvoiceCreated = (inv) => {
    setInvoices(prev => [inv, ...prev]);
    setViewInv(inv);
  };

  const filtered = invoices.filter(i =>
    !search ||
    [i.id, i.customerName, i.vehicleDisplay, i.regNumber, i.bookingId]
      .join(' ').toLowerCase().includes(search.toLowerCase())
  );

  const totalBilled      = invoices.reduce((s, i) => s + Number(i.grandTotal || 0), 0);
  const totalCollected   = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.grandTotal || 0), 0);
  const totalOutstanding = totalBilled - totalCollected;

  return (
    <>
      <TopBar title="Billing" subtitle="Invoices and payment tracking" />
      <div className="admin-content">

        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>Billing & Invoices</h1>
            <p>
              {invoices.filter(i => i.status === 'pending').length} pending ·{' '}
              {invoices.filter(i => i.status === 'paid').length} paid
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowManual(true)}>
              <Plus size={14} /> New Manual Invoice
            </button>
            <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'spinning' : ''} />
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="billing-kpis stagger" style={{ marginBottom: 24 }}>
          {[
            { label: 'Total Billed',   value: inr(totalBilled),     color: 'var(--accent)'  },
            { label: 'Collected',      value: inr(totalCollected),   color: 'var(--success)' },
            { label: 'Outstanding',    value: inr(totalOutstanding), color: 'var(--danger)'  },
            { label: 'Total Invoices', value: invoices.length,       color: 'var(--info)'    },
          ].map(({ label, value, color }) => (
            <div key={label} className="card billing-kpi afu">
              <span className="billing-kpi__val" style={{ color }}>{value}</span>
              <span className="billing-kpi__lbl">{label}</span>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', maxWidth: 440, marginBottom: 20 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input className="input" style={{ paddingLeft: 36 }}
            placeholder="Search by invoice ID, customer, vehicle, booking…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
            <div className="appt-spinner" />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading invoices…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="appt-empty">
            <Clock size={40} />
            <p>{invoices.length === 0
              ? 'No invoices yet. Use "New Manual Invoice" for walk-ins or generate from the Service Tracker.'
              : 'No invoices match your search.'}</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Invoice ID</th><th>Booking</th><th>Date</th><th>Customer</th>
                  <th>Vehicle</th><th>Subtotal</th><th>GST</th><th>Grand Total</th>
                  <th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const { subtotal, gstAmount, grandTotal } = calcTotals(inv);
                  return (
                    <tr key={inv.id}>
                      <td><span style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 12 }}>{inv.id}</span></td>
                      <td>
                        {inv.bookingId?.startsWith('WALKIN-')
                          ? <span className="inv-type-tag" style={{ marginLeft: 0 }}>Walk-in</span>
                          : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.bookingId}</span>}
                      </td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{inv.serviceDate || '—'}</td>
                      <td style={{ fontWeight: 500 }}>{inv.customerName}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.vehicleDisplay} · {inv.regNumber}</td>
                      <td>{inr(subtotal)}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{inr(gstAmount)}</td>
                      <td><span style={{ fontWeight: 700, color: 'var(--accent)' }}>{inr(grandTotal)}</span></td>
                      <td>
                        <span className={`badge ${inv.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                          {inv.status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                          <button className="tbl-btn" title="View" onClick={() => setViewInv(inv)}><Eye size={13} /></button>
                          <button className="tbl-btn" title="Download PDF" onClick={() => downloadInvoicePdf(inv)}><Download size={13} /></button>
                          <button className="tbl-btn" title="Print" onClick={() => printInvoicePdf(inv)}><Printer size={13} /></button>
                          {inv.status === 'pending' && (
                            <button className="btn btn-sm btn-primary" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => handleMarkPaid(inv.id)}>
                              Mark Paid
                            </button>
                          )}
                          <button className="tbl-btn tbl-btn--danger" title="Delete" disabled={deleting[inv.id]} onClick={() => handleDelete(inv.id)}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewInv && <InvoiceViewModal inv={viewInv} onClose={() => setViewInv(null)} onMarkPaid={async id => { await handleMarkPaid(id); }} />}
      {showManual && <ManualInvoiceModal onClose={() => setShowManual(false)} onCreated={handleInvoiceCreated} />}
    </>
  );
}